import type { StatusCategory, TicketPriority, VerifiedVia } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import {
  ActionNeededBadge,
  PriorityBadge,
  RequesterVerificationBadge,
  SlaChip,
  StatusChip,
  mostUrgentSla,
  type ChipSize,
  type IconName,
  type StatusAudience,
} from "@sagot/ui";
import { slaLineText, type ServiceSlaLine } from "./sla-lines";

/** Ticket status in the viewer's language. Without a label it reads the category's default; the client sees "Action needed" where the status waits on them. */
export function ServiceStatusChip({
  category,
  label,
  clientLabel,
  audience = "staff",
  actionNeeded,
  icon,
  size,
}: {
  category: StatusCategory;
  /** The workspace's own label for the status (custom statuses); the category default when omitted. */
  label?: string;
  clientLabel?: string;
  audience?: StatusAudience;
  /** Defaults to true for the `pending` category (the status waits on the client). */
  actionNeeded?: boolean;
  icon?: IconName;
  size?: ChipSize;
}) {
  const { t } = useTranslation();
  return (
    <StatusChip
      category={category}
      label={label ?? t(`service.chips.status.staff.${category}`)}
      clientLabel={clientLabel ?? t(`service.chips.status.client.${category}`)}
      audience={audience}
      actionNeeded={actionNeeded ?? category === "pending"}
      actionNeededLabel={t("service.chips.actionNeeded")}
      icon={icon}
      size={size}
    />
  );
}

export function ServicePriorityBadge({ priority, size }: { priority: TicketPriority; size?: ChipSize }) {
  const { t } = useTranslation();
  return <PriorityBadge priority={priority} label={t(`service.chips.priority.${priority}`)} size={size} />;
}

export function ServiceActionNeededBadge({ size }: { size?: ChipSize }) {
  const { t } = useTranslation();
  return <ActionNeededBadge label={t("service.chips.actionNeeded")} size={size} />;
}

/** Whether the requester proved who they are, and how. A requester without a method reads as plain "Verified". */
export function ServiceRequesterVerification({ verified, method, size }: { verified: boolean; method?: VerifiedVia | null; size?: ChipSize }) {
  const { t } = useTranslation();
  return (
    <RequesterVerificationBadge
      verified={verified}
      label={t(verified ? "service.chips.verification.verified" : "service.chips.verification.unverified")}
      methodLabel={method ? t(`service.chips.verification.method.${method}`) : undefined}
      method={method}
      size={size}
    />
  );
}

/**
 * A ticket's SLA clocks. `compact` (list cell) shows the most urgent one and lists every clock in the tooltip
 * (UX-023); `full` (detail header, context panel) stacks one line per clock.
 */
export function ServiceSlaChip({ lines, variant = "compact" }: { lines: readonly ServiceSlaLine[]; variant?: "compact" | "full" }) {
  const { t } = useTranslation();
  const lineProps = (line: ServiceSlaLine) => ({
    state: line.state,
    text: slaLineText(t, line),
    detail: line.detail,
    percentLeft: line.percentLeft,
    percentLabel: line.percentLeft == null ? undefined : t("service.chips.sla.percentLeft", { percent: Math.round(line.percentLeft) }),
  });
  if (variant === "full") {
    return (
      <div className="flex flex-col gap-2">
        {lines.map((line) => (
          <SlaChip key={line.metric} {...lineProps(line)} />
        ))}
      </div>
    );
  }
  const urgent = mostUrgentSla(lines);
  if (!urgent) {
    return (
      <span className="text-fg-muted">
        <span aria-hidden="true">—</span>
        <span className="sr-only">{t("service.chips.sla.noTargets")}</span>
      </span>
    );
  }
  const tooltip = (
    <div>
      <p className="font-bold">{t("service.chips.sla.allTargets")}</p>
      <ul className="mt-1 space-y-1">
        {lines.map((line) => (
          <li key={line.metric}>{line.percentLeft == null ? slaLineText(t, line) : t("service.chips.sla.targetLine", { text: slaLineText(t, line), percent: Math.round(line.percentLeft) })}</li>
        ))}
      </ul>
    </div>
  );
  return <SlaChip {...lineProps(urgent)} tooltip={tooltip} />;
}
